import { usePostHog } from 'posthog-js/react';
import type React from 'react';
import { type PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { CowWaveLoader } from '../components/CowWaveLoader';
import { ErrorOverlay, type ErrorOverlayProps } from '../components/ErrorOverlay';
import { IS_DEVELOPMENT } from '../helpers';

type MaintenancePayload = Pick<ErrorOverlayProps, 'title' | 'subtitle'>;

export const MaintenanceProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const posthog = usePostHog();
  const isPostHogInitialized = posthog?.__loaded;
  const [isFlagsLoaded, setIsFlagsLoaded] = useState(!isPostHogInitialized);
  const [payload, setPayload] = useState<MaintenancePayload>();

  const loadMaintenancePayload = useCallback(() => {
    if (!isPostHogInitialized) return;
    const payload = posthog.getFeatureFlagPayload('studio-maintenance') as MaintenancePayload;
    setPayload(payload);
  }, [posthog, isPostHogInitialized]);

  useEffect(() => {
    if (!isPostHogInitialized) {
      setIsFlagsLoaded(true);
      return;
    }

    posthog.onFeatureFlags(() => {
      setIsFlagsLoaded(true);
      loadMaintenancePayload();
    });
  }, [posthog, loadMaintenancePayload, isPostHogInitialized]);

  useEffect(() => {
    if (!isPostHogInitialized || !isFlagsLoaded) {
      return;
    }

    const intervalId = window.setInterval(() => {
      posthog.reloadFeatureFlags();
      loadMaintenancePayload();
    }, 60_000);

    return () => window.clearInterval(intervalId);
  }, [loadMaintenancePayload, posthog, isFlagsLoaded, isPostHogInitialized]);

  if (!isFlagsLoaded && !IS_DEVELOPMENT) {
    return <CowWaveLoader centered />;
  }

  if (payload && !IS_DEVELOPMENT) {
    return (
      <ErrorOverlay title={payload.title || 'Studio is currently under maintenance.'} subtitle={payload.subtitle} />
    );
  }

  return children;
};
